// src/features/organizationSlice.ts
import { createSlice } from '@reduxjs/toolkit';
import { organizationsApi } from '~/api/organizations-api';

const PLAN_ID_TO_NAME: Record<number, string> = {
  1: 'launch',
  2: 'standard',
  3: 'premium',
  4: 'custom',
  5: 'partner',
  6: 'demo',
};
function transformSubscriptionResponse(response: any) {
  if (!response?.billing) return response;

  const billing = response.billing;
  const planId = billing.plan;

  return {
    ...response,
    billing: {
      ...billing,
      plan: PLAN_ID_TO_NAME[planId] || 'unknown',
    },
  };
}

// Define the slice state
export interface Billing {
  deletion_date: string | null;
  demo_crawl_limit_enabled?: boolean | null;
  id: string;
  included_conversations: number;
  included_drafts: number;
  included_searches: number;
  included_voice_minutes: number | null;
  intro_enabled: boolean;
  intro_start_date: string | null;
  intro_months: number | null;
  interval_unit: string | null;
  interval_value: number | null;
  price: number;
  start_date: string | null;
  status: string | null;
  plan: string;
  invoice_issuer: string | null;
}
export interface Usage {
  conversations: number;
  crawls: number;
  drafts: number;
  id: string;
  searches: number;
  voice_sessions: number;
  voice_seconds: number;
}
export interface Subscription {
  usage?: Usage;
  billing?: Billing;
}
interface SubscriptionState {
  subscription: Subscription;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: SubscriptionState = {
  subscription: {},
  status: 'idle',
  error: null,
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(organizationsApi.endpoints.getSubscription.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(organizationsApi.endpoints.getSubscription.matchFulfilled, (state, action: any) => {
        state.status = 'succeeded';
        state.subscription = transformSubscriptionResponse(action.payload?.response);
      })
      .addMatcher(organizationsApi.endpoints.getSubscription.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to fetch subscription';
      })

      // Update subscriptions
      .addMatcher(organizationsApi.endpoints.updateSubscription.matchPending, (state) => {
        state.status = 'loading';
      })
      .addMatcher(organizationsApi.endpoints.updateSubscription.matchFulfilled, (state, action: any) => {
        state.status = 'succeeded';
        const updatedSubscription = transformSubscriptionResponse(action.payload?.response);
        if (updatedSubscription) {
          state.subscription = { ...state.subscription, ...updatedSubscription };
        }
      })
      .addMatcher(organizationsApi.endpoints.updateSubscription.matchRejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? 'Failed to update subscription';
      });
  },
});

export default subscriptionSlice.reducer;
